import React, {
  forwardRef,
  Ref,
  InputHTMLAttributes,
  useState,
  useCallback,
  useEffect,
} from "react";

import classnames from "classnames";

import CheckboxIcon from "./CheckboxIcon";
import { StyledCheckbox, StyledInput, StyledLabel } from "./Styles";

export interface CheckBoxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  /** CSS classes */
  className?: string;
  /** Set hover style */
  hover?: boolean;
  /** Id of input and for of label */
  id?: string;
  /** mixed state */
  mixed?: boolean;
  /** On change callback */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Checkbox component
 */
export const Checkbox = forwardRef(
  (
    {
      className,
      hover,
      id = "checkbox",
      mixed = false,
      checked,
      onChange,
      disabled,
      ...rest
    }: CheckBoxProps,
    ref: Ref<HTMLInputElement> | null | undefined
  ) => {
    const [indeterminate, setIndeterminate] = useState(mixed && !checked);
    const [initialChecked, setInitialChecked] = useState(checked);
    const handleChange = useCallback(
      (e) => {
        typeof onChange === "function" && onChange(e);
        setInitialChecked((prev) => !prev);
        setIndeterminate(false);
      },
      [onChange]
    );

    useEffect(() => {
      if (checked !== initialChecked) {
        setInitialChecked(checked);
        setIndeterminate(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checked]);

    useEffect(() => {
      if (mixed !== indeterminate && !checked) {
        setIndeterminate(mixed);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixed]);

    return (
      <StyledCheckbox
        className={classnames(
          "c-checkbox",
          {
            "c-checkbox__hover": hover,
            "c-checkbox__controlled": typeof hover === "boolean",
          },
          className
        )}
        hover={hover}
        tabIndex={-1}
        mixed={indeterminate}
        $disabled={disabled}
      >
        <StyledInput
          role="checkbox"
          id={id}
          className="c-checkbox--input"
          type="checkbox"
          ref={ref}
          {...rest}
          checked={initialChecked}
          onChange={handleChange}
          disabled={disabled}
        />
        <StyledLabel htmlFor={id} className="c-checkbox--label">
          <CheckboxIcon
            data-testid="checkbox-icon"
            height={16}
            width={16}
            mixed={indeterminate}
          />
        </StyledLabel>
      </StyledCheckbox>
    );
  }
);

Checkbox.displayName = "Checkbox";
